export const customerOrderPaths = {
  '/customer-orders': {
    post: {
      summary: 'Create a new customer order',
      operationId: 'createCustomerOrder',
      tags: ['Customer Orders'],
      security: [{ bearerAuth: [] }],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              required: ['association_id', 'property_id', 'customer_type_id', 'requested_documents'],
              properties: {
                association_id: { type: 'integer' },
                property_id: { type: 'integer' },
                customer_type_id: { type: 'integer' },
                processing_type: { 
                  type: 'string',
                  enum: ['Normal', 'Rush'],
                  default: 'Normal'
                },
                requested_documents: {
                  type: 'array',
                  items: {
                    type: 'object',
                    required: ['document_id'],
                    properties: {
                      document_id: { type: 'integer' },
                      fields: {
                        type: 'array',
                        items: {
                          type: 'object',
                          properties: {
                            field_id: { type: 'integer' },
                            value: { type: 'string' },
                            index: { type: 'integer', default: 0 }
                          }
                        }
                      },
                      sections: {
                        type: 'array',
                        items: {
                          type: 'object',
                          properties: {
                            section_id: { type: 'integer' },
                            title: { type: 'string' },
                            order: { type: 'integer' },
                            status: { 
                              type: 'string',
                              enum: ['completed', 'pending'],
                              default: 'pending'
                            },
                            fields: {
                              type: 'array',
                              items: {
                                type: 'object'
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      },
      responses: {
        '201': {
          description: 'Order created successfully',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                properties: {
                  success: { type: 'boolean' },
                  message: { type: 'string' },
                  order: { $ref: '#/components/schemas/Order' }
                }
              }
            }
          }
        },
        '401': {
          description: 'Unauthorized - No authorization header or invalid JWT token',
          content: {
            'application/json': {
              schema: { $ref: '#/components/schemas/ErrorResponse' }
            }
          }
        },
        '400': {
          description: 'Bad request - Missing required fields or invalid data',
          content: {
            'application/json': {
              schema: { $ref: '#/components/schemas/ErrorResponse' }
            }
          }
        }
      }
    },
    get: {
      summary: 'Get all orders for the authenticated customer',
      operationId: 'getCustomerOrders',
      tags: ['Customer Orders'],
      security: [{ bearerAuth: [] }],
      responses: {
        '200': {
          description: 'List of orders',
          content: {
            'application/json': {
              schema: {
                type: 'array',
                items: { $ref: '#/components/schemas/Order' }
              }
            }
          }
        },
        '401': {
          description: 'Unauthorized - No authorization header or invalid JWT token',
          content: {
            'application/json': {
              schema: { $ref: '#/components/schemas/ErrorResponse' }
            }
          }
        }
      }
    }
  },
  '/customer-orders/{id}': {
    get: {
      summary: 'Get a specific customer order',
      operationId: 'getCustomerOrder',
      tags: ['Customer Orders'],
      security: [{ bearerAuth: [] }],
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: { type: 'integer' }
        }
      ],
      responses: {
        '200': {
          description: 'Order details',
          content: {
            'application/json': {
              schema: { $ref: '#/components/schemas/Order' }
            }
          }
        },
        '401': {
          description: 'Unauthorized - No authorization header or invalid JWT token',
          content: {
            'application/json': {
              schema: { $ref: '#/components/schemas/ErrorResponse' }
            }
          }
        },
        '404': {
          description: 'Order not found or access denied',
          content: {
            'application/json': {
              schema: { $ref: '#/components/schemas/ErrorResponse' }
            }
          }
        }
      }
    },
    patch: {
      summary: 'Update a customer order',
      operationId: 'updateCustomerOrder',
      tags: ['Customer Orders'],
      security: [{ bearerAuth: [] }],
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: { type: 'integer' }
        }
      ],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: {
                status: {
                  type: 'string',
                  enum: ['draft', 'submitted', 'processing', 'completed', 'cancelled']
                },
                processing_type: {
                  type: 'string',
                  enum: ['Normal', 'Rush']
                },
                requested_documents: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      document_id: { type: 'integer' },
                      fields: {
                        type: 'array',
                        items: {
                          type: 'object',
                          properties: {
                            field_id: { type: 'integer' },
                            value: { type: 'string' },
                            index: { type: 'integer' }
                          }
                        }
                      },
                      sections: {
                        type: 'array',
                        items: {
                          type: 'object',
                          properties: {
                            section_id: { type: 'integer' },
                            title: { type: 'string' },
                            order: { type: 'integer' },
                            status: { 
                              type: 'string',
                              enum: ['completed', 'pending'] 
                            },
                            fields: {
                              type: 'array',
                              items: {
                                type: 'object'
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      },
      responses: {
        '200': {
          description: 'Order updated successfully',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                properties: {
                  success: { type: 'boolean' },
                  message: { type: 'string' },
                  order: { $ref: '#/components/schemas/Order' }
                }
              }
            }
          }
        },
        '401': {
          description: 'Unauthorized - No authorization header or invalid JWT token',
          content: {
            'application/json': {
              schema: { $ref: '#/components/schemas/ErrorResponse' }
            }
          }
        },
        '404': {
          description: 'Order not found or access denied',
          content: {
            'application/json': {
              schema: { $ref: '#/components/schemas/ErrorResponse' }
            }
          }
        }
      }
    }
  },
  '/customer-orders/{id}/pay': {
    post: {
      summary: 'Process payment for a customer order',
      operationId: 'processOrderPayment',
      tags: ['Customer Orders'],
      security: [{ bearerAuth: [] }],
      parameters: [
        {
          name: 'id',
          in: 'path',
          required: true,
          schema: { type: 'integer' }
        }
      ],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              required: ['payment_reference'],
              properties: {
                payment_reference: {
                  type: 'string',
                  description: 'Payment reference ID from the payment processor'
                }
              }
            }
          }
        }
      },
      responses: {
        '200': {
          description: 'Payment processed successfully',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                properties: {
                  success: { type: 'boolean' },
                  message: { type: 'string' },
                  payment: {
                    type: 'object',
                    properties: {
                      id: { type: 'integer' },
                      order_id: { type: 'integer' },
                      status: { 
                        type: 'string',
                        enum: ['pending', 'paid', 'failed']
                      },
                      payment_method: {
                        type: 'string',
                        enum: ['credit_card', 'bank_transfer']
                      },
                      payment_reference: { type: 'string' },
                      amount: { type: 'number' },
                      created_at: { type: 'string', format: 'date-time' }
                    }
                  }
                }
              }
            }
          }
        },
        '401': {
          description: 'Unauthorized - No authorization header or invalid JWT token',
          content: {
            'application/json': {
              schema: { $ref: '#/components/schemas/ErrorResponse' }
            }
          }
        },
        '404': {
          description: 'Order not found or access denied',
          content: {
            'application/json': {
              schema: { $ref: '#/components/schemas/ErrorResponse' }
            }
          }
        }
      }
    }
  }
};
